import type RootStore from "stores/RootStore";
import type ServiceStore from "stores/ServiceStore";
import { RouteType } from "types/RouteType";

/**
 * Notifications Inbox Route (Slice 1)
 *
 * Full-page inbox at /app/notifications surfacing the unified notification
 * feed produced by the system/notifications service. The route stitches two
 * stores together: notificationsStore (role-filtered server-side) and
 * notificationReadsStore (pinned to the caller's userId server-side) to
 * derive a per-user read/dismissed/archived status.
 *
 * The notifications bell in the global app bar deep-links here.
 */
const NotificationsRoute: RouteType = {
  name: "Notifications",
  path: "/notifications",
  title: "Notifications",
  layout: "/app",
  group: "Notifications",
  requiresAuth: true,
  requiresAdmin: false,
  requiresPermission: false,
  viewportCard: {
    header: {
      component: () => import("./Notifications.Page"),
      title: "Notifications",
      enableRefreshButton: true,
      enableBackButton: true,
    },
    body: {
      component: () => import("./Notifications.Body"),
      grayBackground: true,
    },
  },
  beforeEnter: async ({ rootStore }: { rootStore: RootStore }) => {
    // Warm both stores so the inbox renders instantly from cache. Both services
    // are server-side scoped (role filter on notifications, self-userId pin on
    // notification-reads), so a wide find is safe.
    try {
      const notificationsStore = (
        rootStore as unknown as Record<string, ServiceStore | undefined>
      ).notificationsStore;
      const notificationReadsStore = (
        rootStore as unknown as Record<string, ServiceStore | undefined>
      ).notificationReadsStore;

      await Promise.allSettled([
        notificationsStore?.find?.({
          query: { $limit: 500, $sort: { createdAt: -1 } },
        }),
        notificationReadsStore?.find?.({ query: { $limit: 500 } }),
      ]);
    } catch (error) {
      console.warn("[NotificationsRoute] warm-load failed:", error);
    }
    return true;
  },
};

export default NotificationsRoute;
